import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { OperatorDeviceCommandType, Screen } from '@ghostscreen/contracts';
import { apiErrorMessage } from '../../../i18n';
import { api, ApiRequestError } from '../../../lib/api';
import { getCardConnectionStatus } from '../../../lib/dashboard/screenStatus';
import type { DeviceCommandWireStatus } from '../../fleet/DeviceCommandStatus';
import {
  FIX_COMMANDS,
  phaseForConnection,
  type FixCommandKind,
  type FixRunPhase,
} from './buildFixScreenModel';

export type ActiveFix = {
  kind: FixCommandKind;
  type: OperatorDeviceCommandType;
  commandId: string;
  deviceId: string;
  phase: FixRunPhase;
};

const TERMINAL = new Set(['executed', 'failed', 'expired']);

/**
 * Enqueues Restart / Sync / Clear cache for a paired screen and tracks
 * in-flight command status for the Fix this screen UI.
 */
export function useScreenFixCommands(organisationId: string, screen: Screen) {
  const { t } = useTranslation('displays');
  const queryClient = useQueryClient();
  const [active, setActive] = useState<ActiveFix | null>(null);
  const [wireStatus, setWireStatus] = useState<DeviceCommandWireStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pendingKeyRef = useRef<{ type: OperatorDeviceCommandType; key: string } | null>(null);

  const connection = getCardConnectionStatus(screen);
  const deviceId = screen.deviceId;

  const enqueue = useMutation({
    mutationFn: (variables: {
      kind: FixCommandKind;
      type: OperatorDeviceCommandType;
      idempotencyKey: string;
    }) => {
      if (!deviceId) throw new Error('No device linked');
      return api.sendDeviceCommand(
        organisationId,
        deviceId,
        { type: variables.type },
        variables.idempotencyKey,
      );
    },
    onSuccess: (command, variables) => {
      pendingKeyRef.current = null;
      setError(null);
      setWireStatus(command.status);
      setActive({
        kind: variables.kind,
        type: variables.type,
        commandId: command.id,
        deviceId: command.deviceId,
        phase: phaseForConnection(connection),
      });
      void queryClient.invalidateQueries({ queryKey: ['screens', organisationId] });
      void queryClient.invalidateQueries({ queryKey: ['devices', organisationId] });
    },
    onError: (err) => {
      const isDefinitive4xx =
        err instanceof ApiRequestError && err.status >= 400 && err.status < 500;
      if (isDefinitive4xx) pendingKeyRef.current = null;
      setActive(null);
      setWireStatus(null);
      setError(apiErrorMessage(err, t('fix.sendError')));
    },
  });

  const run = useCallback(
    (kind: FixCommandKind) => {
      const def = FIX_COMMANDS.find((item) => item.kind === kind);
      if (!def || !deviceId) return;
      if (def.type === 'CLEAR_CACHE' || def.type === 'RESTART_PLAYER') {
        const label =
          def.type === 'RESTART_PLAYER'
            ? t('fix.confirm.restart', { name: screen.name })
            : t('fix.confirm.cache', { name: screen.name });
        if (!window.confirm(label)) return;
      }
      setError(null);
      const reuse =
        pendingKeyRef.current?.type === def.type ? pendingKeyRef.current.key : null;
      const idempotencyKey = reuse ?? crypto.randomUUID();
      pendingKeyRef.current = { type: def.type, key: idempotencyKey };
      enqueue.mutate({ kind, type: def.type, idempotencyKey });
    },
    [deviceId, enqueue, screen.name, t],
  );

  const clearLocal = useCallback(() => {
    setActive(null);
    setWireStatus(null);
    setError(null);
    pendingKeyRef.current = null;
  }, []);

  const onStatusChange = useCallback(
    (status: DeviceCommandWireStatus) => {
      setWireStatus(status);
      if (TERMINAL.has(status)) {
        void queryClient.invalidateQueries({ queryKey: ['screens', organisationId] });
        // Keep “busy” until terminal so Hide stays locked; then clear after a beat
        // so the operator sees Done briefly via DeviceCommandStatus.
        window.setTimeout(() => {
          setActive(null);
          setWireStatus(null);
        }, 1600);
      }
    },
    [organisationId, queryClient],
  );

  const inFlight =
    active != null &&
    wireStatus != null &&
    !TERMINAL.has(wireStatus);

  const busy = enqueue.isPending || inFlight || (active != null && wireStatus == null);
  const anyQueued = active?.phase === 'queued' && inFlight;

  return {
    connection,
    active,
    wireStatus,
    error,
    busy,
    anyQueued,
    enqueuePending: enqueue.isPending,
    run,
    clearLocal,
    onStatusChange,
  };
}
